import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Paper,
  Box,
  Divider,
  Autocomplete,
  TextField,
} from "@mui/material";
import axiosInstance from "../components/AxiosInstance";
import { API_BASE_URL, themeStyles } from "../constants";
import { usePermissions } from "../Phase3/CheckPermission";
import OrgChart from "./OrgChart";
import AlertCard from "../components/PopUpWarning";

const StaffHierarchyTree = () => {
  const { giveClientsForGuardian, giveExecutiveDirector } = usePermissions();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [staffHierarchy, setStaffHierarchy] = useState(null);
  const [executiveDirectors, setExecutiveDirectors] = useState([]);

  const roleOrder = [
    "Executive Director",
    "Clinical Director",
    "BCBA",
    "Lead RBT",
    "BT/RBT",
    "Clinical Assistant",
  ];

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const fetchedClients = await giveClientsForGuardian();
      const fetchedDirectors = await giveExecutiveDirector();
      setExecutiveDirectors(fetchedDirectors);
      setClients(fetchedClients);

      if (fetchedClients.length > 0) {
        setSelectedClient(fetchedClients[0]); // ✅ Automatically selects the first client
        fetchStaff(fetchedClients[0], fetchedDirectors);
      }
    } catch (error) {
      console.error("Error fetching clients", error);
    }
  };

  const fetchStaff = async (selectedClient, executiveDirectors) => {
    try {
      const response = await axiosInstance.post(
        API_BASE_URL + "/api/viewallstaff"
      );
      const staffData = response.data;
      let hierarchy = {};

      const assignedStaff =
        selectedClient && selectedClient.assignedUser
          ? staffData.filter((staff) =>
              selectedClient.assignedUser.includes(staff.id + "")
            )
          : [];

      // ✅ Merging executive directors with assigned staff
      const assignedWithDirectors = [
        ...assignedStaff,
        ...executiveDirectors.filter(
          (director) => !assignedStaff.some((staff) => staff.id === director.id)
        ),
      ];

      hierarchy[selectedClient.id] = sortHierarchy(assignedWithDirectors);
      setStaffHierarchy(hierarchy);
    } catch (error) {
      console.error("Error fetching staff data", error);
    }
  };

  const sortHierarchy = (staffList) => {
    return staffList.sort(
      (a, b) => roleOrder.indexOf(a.role) - roleOrder.indexOf(b.role)
    );
  };

  const handleClientChange = (event, newValue) => {
    setSelectedClient(newValue);
    fetchStaff(newValue, executiveDirectors);
  };

  return (
    <Container maxWidth="xl" style={{ padding: "30px", borderRadius: "10px" }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        marginBottom="20px">
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{
            fontFamily: "Poppins, sans-serif",
            color: "#007bff",
            fontSize: "1.7rem",
          }}>
          Parent Dashboard – Your Child’s Learning Journey Begins Here
        </Typography>
      </Box>
      {/* ✅ Center-aligned Autocomplete dropdown */}
      <Box display="flex" justifyContent="center" marginBottom="20px">
        <Autocomplete
          options={clients}
          getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
          value={selectedClient}
          onChange={(event, newValue) => handleClientChange(event, newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Learner *"
              variant="outlined"
              fontSize="1rem"
              sx={{
                ...themeStyles.autocompleteStyles,
              }}
            />
          )}
          sx={{
            width: "50%", // ✅ Reduced width for better UI
          }}
        />
      </Box>

      <Box display="flex" justifyContent="center" gap={4}>
        {staffHierarchy && selectedClient && (
          <Paper
            key={selectedClient.id}
            elevation={8}
            style={{
              padding: "40px",
              borderRadius: "15px",
              marginBottom: "30px",
              backgroundColor: "#ffffe4",
              border: "4px solid #dee2e6",
              boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
              flex: 1,
              textAlign: "center",
            }}>
            <Typography
              variant="h5"
              align="center"
              gutterBottom
              style={{ fontWeight: "bold", color: "#003366" }}>
              Learner: {selectedClient.firstName} {selectedClient.lastName}
            </Typography>
            <Divider
              style={{
                backgroundColor: "#ff80ab",
                height: "3px",
                margin: "10px 0",
              }}
            />
            {staffHierarchy &&
            staffHierarchy[selectedClient.id] &&
            staffHierarchy[selectedClient.id].length > 0 ? (
              <>
                {" "}
                <Typography
                  variant="body1"
                  style={{ marginBottom: "20px", fontWeight: "bold" }}>
                  These are the Facility Members assigned to{" "}
                  {selectedClient.firstName}
                </Typography>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center">
                  <OrgChart data={staffHierarchy[selectedClient.id]} />
                </Box>
              </>
            ) : (
              <AlertCard title={"Facility Members"} />
            )}
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default StaffHierarchyTree;
